// Typed fetch client for cloud-data-service.
//
// All API state on the frontend goes through TanStack Query (`@tanstack/
// react-query`). This module exposes the low-level fetcher; the per-resource
// hooks live in their own files (trajectories.ts, admin.ts, ...).
//
// Defaults:
//   - `staleTime: 5 * 60 * 1000` (5 minutes), per dexhub frontend convention.
//   - Auth header pulled from `lib/auth.ts` on every call.
//   - 60s soft timeout via AbortSignal.timeout. Backend may take longer for
//     `apply_annotation_cuts` polling; for those we set per-call timeouts.

import { authHeaders, buildTarget } from '@/lib/auth';
import type { z } from 'zod';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface ApiClientOptions {
  baseUrl?: string;
}

const DEFAULT_BASE = '/api/v1';

export function apiBase(): string {
  if (typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_BASE) {
    return import.meta.env.VITE_API_BASE as string;
  }
  return DEFAULT_BASE;
}

interface RequestOptions<TIn> {
  path: string;
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: TIn;
  query?: Record<string, string | number | boolean | undefined>;
  signal?: AbortSignal;
  timeoutMs?: number;
}

export async function apiRequest<TOut, TIn = unknown>(
  schema: z.ZodType<TOut> | undefined,
  opts: RequestOptions<TIn>,
): Promise<TOut> {
  const url = new URL(opts.path.startsWith('http') ? opts.path : `${apiBase()}${opts.path}`, location.origin);
  if (opts.query) {
    for (const [k, v] of Object.entries(opts.query)) {
      if (v !== undefined && v !== null && v !== '') url.searchParams.set(k, String(v));
    }
  }

  const controller = new AbortController();
  const timeoutMs = opts.timeoutMs ?? 60_000;
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  if (opts.signal) opts.signal.addEventListener('abort', () => controller.abort(), { once: true });

  let resp: Response;
  try {
    // Cookie auth (credentials:'include') only makes sense in the
    // dexhub-module build, where dexhub has set the dexhub_token cookie on
    // the same parent origin. The standalone build uses Bearer-token auth
    // exclusively (localStorage DEXUPLOAD_TOKEN or future SSO callback),
    // so we omit credentials and rely on the Authorization header. Sending
    // credentials with cross-origin XHR also forces the backend to echo a
    // specific Access-Control-Allow-Origin instead of `*`, which our
    // permissive dev backend doesn't do.
    const credentialsMode: RequestCredentials =
      buildTarget() === 'dexhub-module' ? 'include' : 'omit';

    resp = await fetch(url.toString(), {
      method: opts.method ?? 'GET',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        ...authHeaders(),
      },
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
      signal: controller.signal,
      credentials: credentialsMode,
    });
  } finally {
    clearTimeout(timer);
  }

  if (!resp.ok) {
    let body: unknown;
    try {
      body = await resp.json();
    } catch {
      body = { error: { code: 'unknown', message: resp.statusText } };
    }
    const err = (body as { error?: { code?: string; message?: string; details?: unknown } }).error ?? {};
    throw new ApiError(resp.status, err.code ?? 'unknown', err.message ?? resp.statusText, err.details);
  }

  if (resp.status === 204) return undefined as TOut;
  const json = (await resp.json()) as unknown;
  if (!schema) return json as TOut;
  const parsed = schema.safeParse(json);
  if (!parsed.success) {
    throw new ApiError(500, 'schema_mismatch', 'response did not match Zod schema', parsed.error.issues);
  }
  return parsed.data;
}
